import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import type { Location } from '../../types/Location';

const REFRESH_MS = 30_000;

/** The location's "accepting orders" flag; null until known (or when no single location is selected). */
export function useAcceptingOrders(locationId: string | null) {
    const [accepting, setAccepting] = useState<boolean | null>(null);

    useEffect(() => {
        setAccepting(null);
        if (!locationId) return;
        let cancelled = false;
        const load = () => {
            if (document.hidden) return;
            api.get<Location>(`/locations/${locationId}`)
                .then(({ data }) => {
                    if (!cancelled) setAccepting(data.acceptingOrders);
                })
                .catch(() => undefined);
        };
        load();
        const timer = window.setInterval(load, REFRESH_MS);
        document.addEventListener('visibilitychange', load);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', load);
        };
    }, [locationId]);

    /** Optimistic; rolls back and rethrows on failure. */
    const set = useCallback(
        async (value: boolean) => {
            if (!locationId) return;
            const before = accepting;
            setAccepting(value);
            try {
                await api.put(`/locations/${locationId}/accepting-orders`, { acceptingOrders: value });
            } catch (e) {
                setAccepting(before);
                throw e;
            }
        },
        [locationId, accepting]
    );

    return { accepting, set };
}
